import { FormElement } from "models/forms/formElements/FormElement";
import { FormElementType } from "models/forms/formElements/FormElementType";
import { CheckboxListElement } from "models/forms/formElements/CheckboxListElement";
import { DateOfBirthElement } from "models/forms/formElements/DateOfBirthElement";
import { DescriptionElement } from "models/forms/formElements/DescriptionElement";
import { LabelElement } from "models/forms/formElements/LabelElement";
import { PhoneNumberElement } from "models/forms/formElements/PhoneNumberElement";
import { RadioButtonListElement } from "models/forms/formElements/RadioButtonListElement";
import { TextboxElement } from "models/forms/formElements/TextboxElement";
import { TitleElement } from "models/forms/formElements/TitleElement";
import React, { useEffect, useState } from "react";
import { utilities } from "utils/utilities";
import FormElementRenderer from "../formElements/FormElementRenderer";

// Props for the FormElementList
interface FormElementListEditingProps { // List of form elements to display
  formElements: FormElement[];
  onSave: (elements: FormElement[]) => void;
  isEditing: boolean;
  setFormElements: React.Dispatch<React.SetStateAction<FormElement[]>>;
  //onDelete: (elementId: string) => void;
}

const FormElementListEditing: React.FC<FormElementListEditingProps> = ({ formElements, setFormElements, onSave, isEditing }) => {

  const handleUpdateElement = (updatedElement: FormElement) => {
    setFormElements((prevElements) =>
      prevElements.map((element) =>
        element.id === updatedElement.id ? updatedElement : element
      )
    );
  };

  const handleAddFormElement = (event: React.MouseEvent<HTMLButtonElement>, element: FormElementType) => {
    event?.preventDefault();

    setFormElements((prevElements: FormElement[]) => {
      const nextIndex = (prevElements?.length ?? 0) + 1;
      const newElement = { ...element, id: utilities.generateGUID(), index: nextIndex };
      return [...prevElements, newElement];
    });
  };

  const handleDeleteFormElement = (id: string) => {
    setFormElements((prevElements) => {
      if (prevElements) {
        const updatedElements = prevElements.filter((element) => element.id !== id);
        return updatedElements;
      }
      else {
        return [];
      }
    });
  };

  const staticFormElements: FormElementType[] = [
    //   { id: 'email', name: 'Email', isRequired: true, description: 'Email Element', type: 'email', value: '' } as EmailElement,
    //   //{ id: 'checkbox', name: 'Checkbox List', isRequired: true, description: 'Checkbox Element', type: 'checkbox', checkBoxes: [] } as CheckBoxListElement,
    //   { id: 'dob', name: 'Date of Birth', isRequired: true, description: 'Date of Birth Element', type: 'dob', date: '' } as DateOfBirthElement,
    //   { id: 'document', name: 'Document', isRequired: true, description: 'Document Element', type: 'document', fileName: '', content: null } as DocumentElement,
    //   { id: 'label', name: 'Label', isRequired: false, description: 'Label Element', type: 'label', value: '' } as LabelElement,
    //   { id: 'location', name: 'Location', isRequired: true, description: 'Location Element', type: 'location', addressLine1: '', addressLine2: '', city: '', state: '', country: '', postalCode: '' } as LocationElement,
    //   { id: 'phone', name: 'Phone Number', isRequired: true, description: 'Phone Element', type: 'phone', number: '', areaCode: '', interNationalCode: '' } as PhoneNumberElement,
    //   //{ id: 'radio', name: 'Radio Buttons', isRequired: true, description: 'Radio Buttons Element', type: 'radio', radioButtons: [] } as RadioButtonListElement,
    { id: '00000000-0000-0000-0000-000000000000', index: 1, name: 'Title', isRequired: false, description: 'Title Element', value: 'Section Title', type: "Models.Forms.FormElements.Title" } as TitleElement,
    { id: '00000000-0000-0000-0000-000000000000', index: 1, name: 'Label', isRequired: false, description: 'Label Element', value: 'Helpful hint text', type: "Models.Forms.FormElements.Label" } as LabelElement,
    { id: '00000000-0000-0000-0000-000000000000', index: 1, name: 'Textbox', isRequired: true, description: 'Textbox Element', value: '', placeholder: '', type: "Models.Forms.FormElements.Textbox" } as TextboxElement,
    { id: '00000000-0000-0000-0000-000000000000', index: 1, name: 'Description', isRequired: false, description: 'Description Element', value: '', placeholder: 'Enter your response', type: "Models.Forms.FormElements.Description" } as DescriptionElement,
    { id: '00000000-0000-0000-0000-000000000000', index: 1, name: 'Phone Number', isRequired: false, description: 'Phone Number Element', value: '', placeholder: '555-555-5555', type: "Models.Forms.FormElements.PhoneNumber" } as PhoneNumberElement,
    { id: '00000000-0000-0000-0000-000000000000', index: 1, name: 'Date of Birth', isRequired: false, description: 'Date of Birth Element', value: '', type: "Models.Forms.FormElements.DateOfBirth" } as DateOfBirthElement,
    { id: '00000000-0000-0000-0000-000000000000', index: 1, name: 'Checkbox List', isRequired: false, description: 'Checkbox List Element', value: '', options: ['Option 1', 'Option 2'], type: "Models.Forms.FormElements.CheckBoxList" } as CheckboxListElement,
    { id: '00000000-0000-0000-0000-000000000000', index: 1, name: 'Radio Button List', isRequired: false, description: 'Radio Button List Element', value: '', options: ['Option 1', 'Option 2'], type: "Models.Forms.FormElements.RadioButtonList" } as RadioButtonListElement
  ];

  const handleIndexChange = (index: number, updatedElement: FormElement) => {
    const max = formElements.length;

    updatedElement.index = index >= 1 && index <= max ? index : max;
    setFormElements((prevElements) =>
      prevElements.map((element) =>
        element.id === updatedElement.id ? updatedElement : element
      )
    );
  }

  return (
    <div>
      {(
        <>
          <ul className="space-y-6">
            {formElements
              .sort((a, b) => a.index - b.index)
              .map((element) => (
                <li className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                  <div className="flex justify-between items-start gap-4">
                    {/* Left: FormElementRenderer */}
                    <div className="flex-1">
                      <FormElementRenderer
                        onUpdate={handleUpdateElement}
                        element={element}
                        isEditing={true}
                        onDelete={handleDeleteFormElement}
                      />
                    </div>

                    {/* Right: Order Input and Delete Button */}
                    <div className="flex flex-col items-end gap-2">
                      <label className="text-sm text-gray-700 text-right">
                        <strong>Order: </strong>
                        <input
                          type="number"
                          value={element.index}
                          onChange={(e) => handleIndexChange(Number(e.target.value), element)}
                          className="mt-1 px-2 py-1 w-16 border border-gray-300 rounded text-center"
                        />
                      </label>

                      <button
                        onClick={() => handleDeleteFormElement(element.id)}
                        className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </li>

              ))}
          </ul>
          <div>
            <h3 className="text-lg font-semibold mb-4">Elements to add to your form</h3>
            <ul className="space-y-2">
              {staticFormElements.map((element) => (
                <li key={element.id}>
                  <button
                    type="button"
                    onClick={(event) => handleAddFormElement(event, element)}
                    className="w-full text-left px-4 py-2 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors"
                  >
                    ➕ Add {element.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )
      }
    </div>
  );
};
export default FormElementListEditing;